// src/components/TrackMap.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import ConfirmationDialog from './ConfirmationDialog';
import { updateLastModified } from '../utils/backup';
import '../styles.css';

function TrackMap({ sessionId, onClose }) {
  const [trackMapUrl, setTrackMapUrl] = useState('');
  const [notes, setNotes] = useState([]);
  const [initialNotes, setInitialNotes] = useState([]);
  const [eventName, setEventName] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [vehicleName, setVehicleName] = useState('');
  const [syncStatus, setSyncStatus] = useState('Loading...');
  const [user, loading, error] = useAuthState(auth);
  const [showBubble, setShowBubble] = useState(false);
  const [bubblePos, setBubblePos] = useState({ x: 0, y: 0 });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogContent, setDialogContent] = useState('');
  const [dialogPos, setDialogPos] = useState({ x: 50, y: 50 });
  const [editingNoteIndex, setEditingNoteIndex] = useState(-1);
  const [balanceValues, setBalanceValues] = useState({ tb: 0, en: 0, md: 0, tp: 0, ex: 0 });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const phases = ['tb', 'en', 'md', 'tp', 'ex'];
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const dragIndexRef = useRef(null);
  const touchStartTimeRef = useRef(null);
  const touchStartPosRef = useRef(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (!user || !sessionId) {
        console.warn('Missing user or sessionId for TrackMap');
        setSyncStatus('Error: Missing data');
        return;
      }
      try {
        setSyncStatus('Loading...');
        // Fetch session
        const sessionDoc = await getDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`));
        const session = sessionDoc.exists() ? sessionDoc.data() : null;
        if (!session) {
          console.error('Session not found:', sessionId);
          setSyncStatus('Error: Session not found');
          return;
        }
        setNotes(session.trackNotes || []);
        setInitialNotes(JSON.stringify(session.trackNotes || []));
        setSessionName(session.sessionName || 'Unnamed Session');

        // Fetch vehicle name for the heading pill
        if (session.vehicleId !== undefined && session.vehicleId !== null) {
          try {
            const vDoc = await getDoc(doc(db, `users/${user.uid}/vehicles/${session.vehicleId}`));
            if (vDoc.exists()) setVehicleName(vDoc.data().name || 'Unnamed Vehicle');
          } catch (e) { /* non-fatal */ }
        }

        // Fetch event
        const eventDoc = await getDoc(doc(db, `users/${user.uid}/events/${session.eventId}`));
        const event = eventDoc.exists() ? eventDoc.data() : null;
        setEventName(event?.name || 'Unnamed Event');

        // Fetch circuit
        const circuitName = event?.circuit?.split(' - ')[0] || event?.circuit;
        const circuitsQuery = query(
          collection(db, `users/${user.uid}/circuits`),
          where('name', '==', circuitName)
        );
        const circuitsSnap = await getDocs(circuitsQuery);
        const circuit = circuitsSnap.docs.length > 0 ? circuitsSnap.docs[0].data() : null;
        if (circuit) {
          // Find the layout that matches the event.circuit
          const layoutName = event?.circuit?.split(' - ')[1];
          if (layoutName && circuit.layouts) {
            const layout = circuit.layouts.find(l => l.name === layoutName);
            setTrackMapUrl(layout?.trackMap || circuit.trackMap);
          } else {
            setTrackMapUrl(circuit.trackMap);
          }
        } else {
          console.warn('Circuit not found for event:', event?.circuit);
        }
        setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
      } catch (err) {
        console.error('Error fetching data for TrackMap:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    if (!loading && user) load();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, sessionId]);

  const handleMapClick = (e) => {
    if (showBubble || showDialog) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBubblePos({ x, y });
    setShowBubble(true);
  };

  const handleTouchStart = (e, index) => {
    e.preventDefault();
    dragIndexRef.current = index;
    isDraggingRef.current = false;
    touchStartTimeRef.current = Date.now();
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (e) => {
    if (dragIndexRef.current === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = imageRef.current.getBoundingClientRect();
    const deltaX = touch.clientX - touchStartPosRef.current.x;
    const deltaY = touch.clientY - touchStartPosRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance > 10) {
      isDraggingRef.current = true;
      const x = ((touch.clientX - rect.left) / rect.width) * 100;
      const y = ((touch.clientY - rect.top) / rect.height) * 100;
      const updatedNotes = [...notes];
      updatedNotes[dragIndexRef.current].position = { x, y };
      setNotes(updatedNotes);
    }
  };

  const handleTouchEnd = (e) => {
    if (dragIndexRef.current === null) return;
    if (!isDraggingRef.current) {
      const touchDuration = Date.now() - touchStartTimeRef.current;
      if (touchDuration < 300) {
        handleNoteClick(dragIndexRef.current);
      }
    }
    dragIndexRef.current = null;
    isDraggingRef.current = false;
    touchStartTimeRef.current = null;
    touchStartPosRef.current = null;
  };

  const handleAddNote = (type) => {
    setShowBubble(false);
    setDialogType(type);
    setDialogContent(type === 'text' ? '' : { tb: 0, en: 0, md: 0, tp: 0, ex: 0 });
    setDialogPos(bubblePos);
    setShowDialog(true);
    setEditingNoteIndex(-1);
    setBalanceValues({ tb: 0, en: 0, md: 0, tp: 0, ex: 0 });
  };

  const handleDialogSave = () => {
    const newNote = {
      type: dialogType,
      position: dialogPos,
      content: dialogType === 'text' ? dialogContent : balanceValues
    };
    let updatedNotes;
    if (editingNoteIndex >= 0) {
      updatedNotes = [...notes];
      updatedNotes[editingNoteIndex] = newNote;
    } else {
      updatedNotes = [...notes, newNote];
    }
    setNotes(updatedNotes);
    setShowDialog(false);
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  const handleNoteClick = (index) => {
    if (isDraggingRef.current) return;
    const note = notes[index];
    setDialogType(note.type);
    setDialogContent(note.type === 'text' ? note.content : '');
    setBalanceValues(note.type === 'balance' ? { ...note.content } : balanceValues);
    setDialogPos(note.position);
    setShowDialog(true);
    setEditingNoteIndex(index);
  };

  const handleSave = async () => {
    if (!user || !sessionId) return;
    try {
      setSyncStatus('Saving...');
      await setDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`), { trackNotes: notes }, { merge: true });
      await updateLastModified(user);
      setInitialNotes(JSON.stringify(notes));
      setSyncStatus('Synced');
      onClose();
    } catch (err) {
      console.error('Error saving track notes:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert(`Failed to save track notes: ${err.message}`);
    }
  };

  const handleCancel = () => {
    if (JSON.stringify(notes) !== initialNotes) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('index', index);
    const img = new Image();
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const index = parseInt(e.dataTransfer.getData('index'));
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const updatedNotes = [...notes];
    updatedNotes[index].position = { x, y };
    setNotes(updatedNotes);
  };

  const handleDeleteNote = () => {
    setShowDeleteConfirm(false);
    setShowDialog(false);
    if (editingNoteIndex >= 0) {
      const updatedNotes = notes.filter((_, i) => i !== editingNoteIndex);
      setNotes(updatedNotes);
    }
  };

  const renderNote = (note, index) => {
    const style = {
      position: 'absolute',
      left: `${note.position.x}%`,
      top: `${note.position.y}%`,
      cursor: 'move',
      zIndex: 10,
      transform: 'translate(-50%, -50%)'
    };
    if (note.type === 'text') {
      return (
        <div
          className="track-map-note"
          style={style}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onTouchStart={(e) => handleTouchStart(e, index)}
          onTouchEnd={handleTouchEnd}
          onClick={() => handleNoteClick(index)}
        >
          {note.content}
        </div>
      );
    } else {
      return (
        <div
          className="track-map-note"
          style={style}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onTouchStart={(e) => handleTouchStart(e, index)}
          onTouchEnd={handleTouchEnd}
          onClick={() => handleNoteClick(index)}
        >
          {renderGraph(note.content, false)}
        </div>
      );
    }
  };

  const renderGraph = (values, isDialog = false) => {
    const viewWidth = 200;
    const viewHeight = 140;
    const svgWidth = isDialog ? viewWidth : viewWidth / 2;
    const svgHeight = isDialog ? viewHeight : viewHeight / 2;
    const graphHeight = 129;
    const midY = graphHeight / 2;
    const textY = graphHeight + 15;
    const padding = 6;
    const points = phases.map((ph, i) => ({
      x: (i / (phases.length - 1)) * viewWidth,
      y: midY - (values[ph] * (midY / 5)),
    }));

    const mdX = (2 / (phases.length - 1)) * viewWidth;
    const strokeWidth = 4;
    const radius = 15; // Increased radius for larger arcs

    let pathD = points.reduce((acc, point, i, a) => {
      if (i === 0) return `M ${point.x.toFixed(2)},${point.y.toFixed(2)}`;
      if (i === a.length - 1) return `${acc} L ${point.x.toFixed(2)},${point.y.toFixed(2)}`;

      const c1 = a[i - 1];
      const c2 = point;
      const c3 = a[i + 1];

      const c1c2Dist = Math.hypot(c2.x - c1.x, c2.y - c1.y);
      const c2c3Dist = Math.hypot(c3.x - c2.x, c3.y - c2.y);
      const effectiveRadius = Math.min(radius, c1c2Dist / 2, c2c3Dist / 2);

      const c1c2Ratio = (c1c2Dist - effectiveRadius) / c1c2Dist;
      const c1c2Curve = {
        x: ((1 - c1c2Ratio) * c1.x + c1c2Ratio * c2.x).toFixed(2),
        y: ((1 - c1c2Ratio) * c1.y + c1c2Ratio * c2.y).toFixed(2)
      };

      const c2c3Ratio = effectiveRadius / c2c3Dist;
      const c2c3Curve = {
        x: ((1 - c2c3Ratio) * c2.x + c2c3Ratio * c3.x).toFixed(2),
        y: ((1 - c2c3Ratio) * c2.y + c2c3Ratio * c3.y).toFixed(2)
      };

      return `${acc} L ${c1c2Curve.x},${c1c2Curve.y} Q ${c2.x.toFixed(2)},${c2.y.toFixed(2)} ${c2c3Curve.x},${c2c3Curve.y}`;
    }, '');

    return (
      <svg width={svgWidth} height={svgHeight} viewBox={`0 ${-padding} ${viewWidth} ${viewHeight + padding * 2}`}>
        <defs>
          <linearGradient id="balanceGradient" gradientUnits="userSpaceOnUse" x1="0" x2="0" y1="0" y2={graphHeight}>
            <stop offset="0%" stopColor="red" />
            <stop offset="35%" stopColor="orange" />
            <stop offset="50%" stopColor="green" />
            <stop offset="60%" stopColor="yellow" />
            <stop offset="100%" stopColor="brown" />
          </linearGradient>
        </defs>
        <line x1="0" y1={midY} x2={viewWidth} y2={midY} stroke="gray" strokeDasharray="2" />
        <line x1={mdX} y1="0" x2={mdX} y2={graphHeight} stroke="gray" strokeDasharray="2" />
        <path
          d={pathD}
          fill="none"
          stroke="url(#balanceGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {phases.map((ph, i) => (
          <text
            key={ph}
            x={(i / (phases.length - 1)) * viewWidth}
            y={textY}
            textAnchor="middle"
            fontSize="15px"
          >
            {ph.toUpperCase()}
          </text>
        ))}
        {isDialog && (
          <>
            <text x="5" y="10" fontSize="15px" textAnchor="start">OS</text>
            <text x="5" y={midY + 5} fontSize="15px" textAnchor="start">N</text>
            <text x="5" y={graphHeight - 5} fontSize="15px" textAnchor="start">US</text>
          </>
        )}
      </svg>
    );
  };

  const handleSliderChange = (ph, val) => {
    setBalanceValues({ ...balanceValues, [ph]: parseInt(val) });
  };

  const renderDialog = () => {
    const title = dialogType === 'text' ? 'Text Note' : 'Balance Note';
    return (
      <div className="overlay-card">
        <div className="card-container">
          <div className="page-title">
            <h2>{title}</h2>
            <button onClick={handleDialogCancel} className="close-button">✕</button>
          </div>
          {dialogType === 'text' ? (
            <textarea value={dialogContent} onChange={e => setDialogContent(e.target.value)} className="event-editor-textarea" />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '200px', marginBottom: '20px' }}>
                {phases.map(ph => (
                  <div key={ph} style={{ textAlign: 'center' }}>
                    <input
                      type="range"
                      min="-5"
                      max="5"
                      value={balanceValues[ph]}
                      onChange={e => handleSliderChange(ph, e.target.value)}
                      style={{ height: '150px', writingMode: 'vertical-lr', direction: 'rtl', margin: '0 10px' }}
                    />
                    <label style={{ display: 'block', textAlign: 'center', marginTop: '5px' }}>{ph.toUpperCase()}</label>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {renderGraph(balanceValues, true)}
              </div>
            </>
          )}
          <div className="event-editor-actions">
            <button onClick={handleDialogSave} className="btn btn-save">Save</button>
            {editingNoteIndex >= 0 && (
              <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-danger">Delete</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleOutsideClick = (e) => {
    if (showBubble && !e.target.closest('.bubble')) {
      setShowBubble(false);
    }
  };

  return (
    <div className="overlay-card" onClick={handleOutsideClick}>
      <div className="card-container track-map-container">
        <div className="page-title">
          <h2>{eventName}- {sessionName}{vehicleName && <span className="session-info-vehicle">{vehicleName}</span>}</h2>
          <button onClick={handleCancel} className="close-button">✕</button>
        </div>
        <div className="track-map-wrapper" ref={containerRef}>
          {syncStatus === 'Loading...' ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              flexDirection: 'column'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '20px' }}>Loading Track Map...</p>
            </div>
          ) : trackMapUrl ? (
            <img
              ref={imageRef}
              src={trackMapUrl}
              alt="Track Map"
              className="track-map-image"
              onClick={handleMapClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px'
            }}>
              <p>No Track Map Available</p>
            </div>
          )}
          {notes.map((note, index) => (
            <React.Fragment key={index}>
              {renderNote(note, index)}
            </React.Fragment>
          ))}
          {showBubble && (
            <div
              className="bubble track-map-bubble"
              style={{
                position: 'absolute',
                left: `${bubblePos.x}%`,
                top: `${bubblePos.y}%`,
                zIndex: 100,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <button onClick={() => handleAddNote('text')} className="btn btn-secondary track-map-add-btn">Add Note</button>
              <button onClick={() => handleAddNote('balance')} className="btn btn-secondary track-map-add-btn">Add Balance Note</button>
            </div>
          )}
        </div>
        <div className="session-buttons">
          <button onClick={handleSave} className="btn btn-save">Save</button>
          <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
      {showDialog && renderDialog()}
      {showDeleteConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to delete this note?"
          onConfirm={handleDeleteNote}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      {showDiscardConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to discard unsaved changes?"
          onConfirm={confirmDiscard}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}
    </div>
  );
}

export default TrackMap;